/**
 * Plano 9 / A9-02 — Card "Nó vivo?" (3×3) para Cortex OS.
 * Mostra status verde/amarelo/vermelho de cada nó com base em
 * volume de eventos / pipeline_runs nas últimas 24h.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId, useUserOrgs, setActiveOrgId } from "@/hooks/useSkillOS";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, ArrowRightLeft } from "lucide-react";
import { cn } from "@/lib/utils";

type NodeStatus = "green" | "yellow" | "red";

interface NodeInfo {
  key: string;
  label: string;
  count: number;
  status: NodeStatus;
}

const NODE_DEFS: { key: string; label: string; threshold_green: number }[] = [
  { key: "input_gating", label: "Input Gating", threshold_green: 5 },
  { key: "threat", label: "Threat Detection", threshold_green: 1 },
  { key: "conflict", label: "Conflict Detection", threshold_green: 1 },
  { key: "pattern_sep", label: "Pattern Separation", threshold_green: 1 },
  { key: "tom", label: "Theory of Mind", threshold_green: 1 },
  { key: "metacog", label: "Metacognition", threshold_green: 1 },
  { key: "inhibitor", label: "Response Inhibitor", threshold_green: 5 },
  { key: "group_state", label: "Group State", threshold_green: 1 },
  { key: "affect", label: "Affect Mode", threshold_green: 1 },
];

function classify(count: number, greenAt: number): NodeStatus {
  if (count >= greenAt) return "green";
  if (count > 0) return "yellow";
  return "red";
}

const STATUS_STYLES: Record<NodeStatus, string> = {
  green: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-300",
  yellow: "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-300",
  red: "bg-rose-500/15 text-rose-700 border-rose-500/30 dark:text-rose-300",
};

export function CortexLiveNodes() {
  const qc = useQueryClient();
  const { data: orgId } = useCurrentOrgId();
  const { data: orgs = [] } = useUserOrgs();
  const currentOrg = orgs.find((o) => o.org_id === orgId);
  const otherActiveOrg = orgs.find((o) => o.org_id !== orgId && o.active_groups > 0);
  const { data, isLoading } = useQuery({
    queryKey: ["cortex-live-nodes-24h", orgId],
    enabled: !!orgId,
    refetchInterval: 30_000,
    queryFn: async (): Promise<NodeInfo[]> => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      // Conta eventos por tipo nas últimas 24h (proxies seguros)
      const tables = [
        "input_gating_decisions",
        "threat_detections",
        "conflict_detections",
        "consolidated_memories",         // Pattern Separation proxy (memórias separadas/consolidadas)
        "tom_models",                    // ToM (filter by updated_at)
        "confidence_calibration_runs",   // Metacognition proxy
        "inhibition_decisions",
        "group_state_classifications",
        "affect_states",
      ] as const;
      const keys = ["input_gating","threat","conflict","pattern_sep","tom","metacog","inhibitor","group_state","affect"];
      const dateField = ["created_at","created_at","created_at","updated_at","updated_at","created_at","created_at","created_at","created_at"];

      const results = await Promise.all(
        tables.map((t, i) =>
          (supabase as any).from(t).select("id", { count: "exact", head: true }).eq("org_id", orgId).gte(dateField[i], since)
            .then((r: any) => r?.count ?? 0)
            .catch(() => 0),
        ),
      );

      // Conflict Detection (índice 2) — multi-source fallback:
      // tabela conflict_detections + cortex_events conflict.* (inhibition, etc).
      {
        const { count: evCount } = await (supabase as any)
          .from("cortex_events")
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId)
          .gte("occurred_at", since)
          .like("event_type", "conflict.%");
        results[2] = (results[2] as number) + (evCount ?? 0);
      }

      // Metacognition (índice 5) — multi-source fallback:
      // confidence_calibration_runs + metacog_runtime_decisions (gate runtime principal)
      // + skill_executions com metacog_decision + cortex_events metacog.*
      if ((results[5] as number) === 0) {
        const [runtimeRes, skillRes, eventsRes] = await Promise.all([
          (supabase as any)
            .from("metacog_runtime_decisions")
            .select("id", { count: "exact", head: true })
            .eq("org_id", orgId)
            .gte("created_at", since)
            .then((r: any) => r?.count ?? 0)
            .catch(() => 0),
          (supabase as any)
            .from("skill_executions")
            .select("id", { count: "exact", head: true })
            .eq("org_id", orgId)
            .gte("created_at", since)
            .not("metacog_decision", "is", null)
            .then((r: any) => r?.count ?? 0)
            .catch(() => 0),
          (supabase as any)
            .from("cortex_events")
            .select("id", { count: "exact", head: true })
            .eq("org_id", orgId)
            .gte("occurred_at", since)
            .like("event_type", "metacog.%")
            .then((r: any) => r?.count ?? 0)
            .catch(() => 0),
        ]);
        results[5] = runtimeRes + skillRes + eventsRes;
      }

      const counts: Record<string, number> = {};
      keys.forEach((k, i) => { counts[k] = results[i] ?? 0; });

      return NODE_DEFS.map((n) => ({
        key: n.key,
        label: n.label,
        count: counts[n.key] ?? 0,
        status: classify(counts[n.key] ?? 0, n.threshold_green),
      }));
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="w-4 h-4" />
          Nó vivo? — últimas 24h
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2">
          {(data ?? NODE_DEFS.map((n) => ({ ...n, count: 0, status: "red" as NodeStatus }))).map((n) => (
            <div
              key={n.key}
              className={cn(
                "rounded-lg border px-3 py-2 flex items-center justify-between gap-2",
                STATUS_STYLES[n.status],
              )}
            >
              <span className="text-xs font-medium truncate">{n.label}</span>
              <Badge variant="outline" className="bg-background/50 text-xs">
                {isLoading ? "…" : n.count}
              </Badge>
            </div>
          ))}
        </div>
        {(() => {
          const total = (data ?? []).reduce((sum, n) => sum + n.count, 0);
          const allZero = !!data && total === 0;
          return (
            <>
              {allZero && otherActiveOrg && (
                <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    Sem dados em <strong>{currentOrg?.name ?? "esta org"}</strong>. Você tem atividade em <strong>{otherActiveOrg.name}</strong>.
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1"
                    onClick={() => {
                      setActiveOrgId(otherActiveOrg.org_id);
                      qc.invalidateQueries({ queryKey: ["skill-os", "current-org"] });
                      qc.invalidateQueries();
                    }}
                  >
                    <ArrowRightLeft className="w-3 h-3" />
                    Trocar
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2 flex items-center justify-between gap-2">
                <span>
                  {!orgId
                    ? "Nenhuma organização ativa encontrada."
                    : "Verde: ≥ limiar · Amarelo: vivo mas baixo · Vermelho: 0 eventos"}
                </span>
                {currentOrg && (
                  <span className="font-mono">
                    Org: <strong>{currentOrg.name}</strong> · {total} eventos
                  </span>
                )}
              </p>
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
}
